import React from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import classNames from 'classnames';
import noop from 'lodash/noop';

import { Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';
import Dropdown from '@adsk/bim360-matrix-react-dropdown';
import DropdownMenu from '@adsk/bim360-matrix-react-dropdown-menu';
import A from '@adsk/bim360-matrix-react-a';
import AnchorButton from '@adsk/bim360-matrix-react-anchor-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import OverflowTooltip from '@adsk/bim360-matrix-react-overflow-tooltip';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

import helpIconSvg from '@adsk/bim360-matrix-react-assets/help-icon.svg';
import alloyQuestionFilled from '@adsk/bim360-matrix-react-assets/alloy-question-filled.svg';

import './_MatrixHelpMenu.scss';

export class MatrixHelpMenu extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false
    };

    this.defaultItems = {
      [MatrixHelpMenu.ItemTypes.COMMUNITY]: {
        url: 'https://forums.autodesk.com/t5/bim-360/ct-p/2025',
        target: '_blank'
      },
      [MatrixHelpMenu.ItemTypes.SUBMIT_YOUR_IDEA]: {
        url: 'https://forums.autodesk.com/t5/bim-360-ideas/idb-p/2032',
        target: '_blank'
      },
      [MatrixHelpMenu.ItemTypes.CONTACT_SUPPORT]: {
        target: '_blank'
      },
      [MatrixHelpMenu.ItemTypes.HELP]: {
        target: '_blank'
      }
    };

    this.translations = {
      [MatrixHelpMenu.ItemTypes.HELP]: 'HELP',
      [MatrixHelpMenu.ItemTypes.GET_STARTED]: 'GET_STARTED',
      [MatrixHelpMenu.ItemTypes.ABOUT]: 'ABOUT',
      [MatrixHelpMenu.ItemTypes.COMMUNITY]: 'COMMUNITY',
      [MatrixHelpMenu.ItemTypes.SUBMIT_YOUR_IDEA]: 'SUBMIT_YOUR_IDEA',
      [MatrixHelpMenu.ItemTypes.CONTACT_SUPPORT]: 'CONTACT_SUPPORT'
    };
  }

  getModuleType() {
    return this.props.selectedModule.type || this.props.selectedModule.id;
  }

  getHandler(moduleType, itemType) {
    const itemTypeHandlers =
      (this.props.helpMenuContextualItems &&
        this.props.helpMenuContextualItems[moduleType] &&
        this.props.helpMenuContextualItems[moduleType][itemType]) ||
      {};
    return {
      ...this.defaultItems[itemType],
      ...itemTypeHandlers
    };
  }

  checkIfShouldRenderItem(moduleType, itemType) {
    const currentHandler = this.getHandler(moduleType, itemType);
    return !currentHandler.hide && !!(currentHandler.onClick || currentHandler.url);
  }

  renderContextualItem(moduleType, itemType, titlePrefix = '') {
    const title = `${titlePrefix}${this.props[TSymbolProp].translate(this.translations[itemType])}`;
    const currentHandler = this.getHandler(moduleType, itemType);

    return (
      <li>
        {currentHandler.url ? (
          <A
            className="MatrixHelpMenu__list-item"
            onClick={currentHandler.onClick}
            href={currentHandler.url}
            target={currentHandler.target || '_blank'}
          >
            <OverflowTooltip
              tooltipClassName="HelpMenMatrixHelpMenu__list-item-tooltip"
              autoTipOnEllipsis
              singleLine={false}
            >
              {title}
            </OverflowTooltip>
          </A>
        ) : (
          <AnchorButton className="MatrixHelpMenu__list-item" onClick={currentHandler.onClick}>
            {title}
          </AnchorButton>
        )}
      </li>
    );
    /* eslint-enable react/jsx-handler-names */
  }

  render() {
    const { theme } = this.props;

    this.defaultItems[MatrixHelpMenu.ItemTypes.CONTACT_SUPPORT].url = this.props[
      TSymbolProp
    ].translate('DEFAULT_CONTACT_SUPPORT_URL');
    this.defaultItems[MatrixHelpMenu.ItemTypes.HELP].url = this.props[TSymbolProp].translate(
      'DEFAULT_HELP_URL'
    );

    const moduleType = this.props.selectedModule && this.getModuleType();
    const shouldRender = Object.keys(MatrixHelpMenu.ItemTypes).reduce((seed, typeKey) => {
      const type = MatrixHelpMenu.ItemTypes[typeKey];
      return { ...seed, [type]: this.checkIfShouldRenderItem(moduleType, type) };
    }, {});
    const shouldRenderSeparator =
      shouldRender[MatrixHelpMenu.ItemTypes.HELP] ||
      shouldRender[MatrixHelpMenu.ItemTypes.GET_STARTED] ||
      shouldRender[MatrixHelpMenu.ItemTypes.ABOUT];

    const helpIconClasses = classNames('MatrixHelpMenu__help-icon', {
      'MatrixHelpMenu__help-icon--expanded': this.state.isOpen,
      'MatrixHelpMenu__help-icon--alloy': theme === Themes.ALLOY
    });

    return (
      <div className="MatrixHelpMenu">
        <Dropdown
          className="MatrixHelpMenu__dropdown"
          onSelect={noop}
          disabled={!this.props.selectedModule}
          onToggleMenu={() => {
            this.setState({ isOpen: !this.state.isOpen });
          }}
        >
          <IconButton disabled={!this.props.selectedModule} className={helpIconClasses}>
            {this.props.theme === Themes.ALLOY ? (
              <SvgIcon height="20px" width="20px">
                {alloyQuestionFilled}
              </SvgIcon>
            ) : (
              <SvgIcon height="24px" width="24px">
                {helpIconSvg}
              </SvgIcon>
            )}
          </IconButton>
          <DropdownMenu float={DropdownMenu.FloatOptions.LEFT}>
            {shouldRender[MatrixHelpMenu.ItemTypes.HELP] &&
              this.renderContextualItem(
                moduleType,
                MatrixHelpMenu.ItemTypes.HELP,
                `${this.props.selectedModule && this.props.selectedModule.name} `
              )}
            {shouldRender[MatrixHelpMenu.ItemTypes.GET_STARTED] &&
              this.renderContextualItem(moduleType, MatrixHelpMenu.ItemTypes.GET_STARTED)}
            {shouldRender[MatrixHelpMenu.ItemTypes.ABOUT] &&
              this.renderContextualItem(moduleType, MatrixHelpMenu.ItemTypes.ABOUT)}
            {shouldRenderSeparator && <div className="MatrixHelpMenu__separator-line" />}
            {shouldRender[MatrixHelpMenu.ItemTypes.COMMUNITY] &&
              this.renderContextualItem(moduleType, MatrixHelpMenu.ItemTypes.COMMUNITY)}
            {shouldRender[MatrixHelpMenu.ItemTypes.SUBMIT_YOUR_IDEA] &&
              this.renderContextualItem(moduleType, MatrixHelpMenu.ItemTypes.SUBMIT_YOUR_IDEA)}
            {shouldRender[MatrixHelpMenu.ItemTypes.CONTACT_SUPPORT] &&
              this.renderContextualItem(moduleType, MatrixHelpMenu.ItemTypes.CONTACT_SUPPORT)}
          </DropdownMenu>
        </Dropdown>
      </div>
    );
  }
}

MatrixHelpMenu.defaultProps = {
  theme: Themes.MATRIX
};

MatrixHelpMenu.propTypes = {
  /**
   * The locale to use for all the header constant strings
   */
  locale: PropTypes.string.isRequired,
  /**
   * The selected module object
   */
  selectedModule: PropTypes.shape({
    /**
     * The name of the module (used in case we're rendering a help context that's specific to the module).
     */
    name: PropTypes.string.isRequired,
    /**
     * The type of the module - should be used, this is a newer api.
     */
    type: PropTypes.string,
    /**
     * the id of the module - should not be used, this is an old api.
     */
    id: PropTypes.string
  }),
  /**
   * object containing the module-contextual help items handlers. Should be in the following structure:
   * { [module_id (as received from hq entitlements)]: {
   *       [MatrixHelpMenu.ItemTypes.HELP]: { url (optional): 'http://url.com' },
   *       [MatrixHelpMenu.ItemTypes.GET_STARTED]: { onClick (optional): () => {} },
   *       [MatrixHelpMenu.ItemTypes.ABOUT]: { onClick (optional): () => {} },
   *       [MatrixHelpMenu.ItemTypes.COMMUNITY]: { hide: true },
   *    }
   * }
   *
   * You don't need to have all of the ItemTypes - if an item type doesn't exist it will not be rendered
   * unless this item has a default value. If url is passed then the item will be a link item that will
   * be opened in a new tab (you can still pass onClick to be triggered in addition).
   */
  helpMenuContextualItems: PropTypes.objectOf(
    PropTypes.shape({
      /**
       * The Help contextual item defining url to open or/and onClick to run
       */
      help: PropTypes.shape({
        url: PropTypes.string,
        target: PropTypes.string,
        onClick: PropTypes.func,
        hide: PropTypes.bool
      }),
      /**
       * The Get Started contextual item defining url to open or/and onClick to run
       */
      getStarted: PropTypes.shape({
        url: PropTypes.string,
        target: PropTypes.string,
        onClick: PropTypes.func,
        hide: PropTypes.bool
      }),
      /**
       * The About contextual item defining url to open or/and onClick to run
       */
      about: PropTypes.shape({
        url: PropTypes.string,
        target: PropTypes.string,
        onClick: PropTypes.func,
        hide: PropTypes.bool
      }),
      /**
       * The Community contextual item defining url to open or/and onClick to run
       */
      community: PropTypes.shape({
        url: PropTypes.string,
        target: PropTypes.string,
        onClick: PropTypes.func,
        hide: PropTypes.bool
      }),
      /**
       * The About contextual item defining url to open or/and onClick to run
       */
      submitYourIdea: PropTypes.shape({
        url: PropTypes.string,
        target: PropTypes.string,
        onClick: PropTypes.func,
        hide: PropTypes.bool
      }),
      /**
       * The Contact Support contextual item defining url to open or/and onClick to run
       */
      contactSupport: PropTypes.shape({
        url: PropTypes.string,
        target: PropTypes.string,
        onClick: PropTypes.func,
        hide: PropTypes.bool
      })
    })
  ),
  theme: PropTypes.oneOf(objectValues(Themes))
};

MatrixHelpMenu.ItemTypes = {
  HELP: 'help',
  GET_STARTED: 'getStarted',
  ABOUT: 'about',
  COMMUNITY: 'community',
  SUBMIT_YOUR_IDEA: 'submitYourIdea',
  CONTACT_SUPPORT: 'contactSupport'
};

export default withLocale(
  MatrixHelpMenu,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
